import React, { useState, useEffect } from 'react';
import { MaintenanceCompany } from '../../types';
import { api } from '../../services/api';
import { Building2, Phone, Mail, Plus, Search, Hammer, Edit, Trash2 } from 'lucide-react';

const MaintenanceCompanies: React.FC = () => {
    const [companies, setCompanies] = useState<MaintenanceCompany[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCompany, setEditingCompany] = useState<MaintenanceCompany | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Form State
    const [formData, setFormData] = useState<Partial<MaintenanceCompany>>({
        name: '', specialty: '', contactName: '', phone: '', email: '', status: 'Active'
    });

    useEffect(() => {
        loadCompanies();
    }, []);

    const loadCompanies = async () => {
        setIsLoading(true);
        try {
            const data = await api.maintenance.companies.getAll();
            setCompanies(data);
        } catch (error) {
            console.error('Failed to load companies', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenEdit = (company: MaintenanceCompany) => {
        setEditingCompany(company);
        setFormData({ ...company });
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingCompany(null);
        setFormData({ name: '', specialty: '', contactName: '', phone: '', email: '', status: 'Active' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingCompany) {
                await api.maintenance.companies.update(editingCompany.id, formData as MaintenanceCompany);
                alert('تم تحديث بيانات الشركة بنجاح');
            } else {
                await api.maintenance.companies.create(formData as MaintenanceCompany);
                alert('تم إضافة الشركة بنجاح');
            }
            handleCloseForm();
            loadCompanies();
        } catch (error) {
            alert('فشل حفظ بيانات الشركة');
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.confirm('هل أنت متأكد من رغبتك في حذف هذه الشركة؟')) return;
        try {
            await api.maintenance.companies.delete(id);
            loadCompanies();
            alert('تم حذف الشركة بنجاح');
        } catch (error) {
            alert('فشل في حذف الشركة');
        }
    };

    const filteredCompanies = companies.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        c.specialty.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input 
                        type="text" 
                        placeholder="بحث عن شركة صيانة..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-xl py-2 pr-10 pl-4 focus:ring-2 focus:ring-fly-500/20 focus:border-fly-500 outline-none"
                    />
                </div>
                <button 
                    onClick={() => setShowForm(true)}
                    className="bg-fly-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-fly-700 transition-all font-bold shadow-lg shadow-fly-600/20"
                >
                    <Plus size={18} />
                    إضافة شركة
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-10"><div className="w-8 h-8 border-4 border-fly-200 border-t-fly-600 rounded-full animate-spin"></div></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCompanies.map(company => (
                        <div key={company.id} className={`bg-white p-5 rounded-2xl border ${company.status === 'Inactive' ? 'opacity-60 bg-slate-50 border-slate-200' : 'border-slate-100'} shadow-sm hover:shadow-md transition-all group relative`}>
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2.5 rounded-xl transition-colors ${company.status === 'Inactive' ? 'bg-slate-200' : 'bg-fly-50 group-hover:bg-fly-600'}`}>
                                        <Building2 size={20} className={`${company.status === 'Inactive' ? 'text-slate-500' : 'text-fly-600 group-hover:text-white transition-colors'}`} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-800">{company.name}</h3>
                                        <div className="flex gap-2 items-center">
                                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">{company.specialty}</span>
                                            {company.status === 'Inactive' && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">غير نشط</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => handleOpenEdit(company)} className="p-1.5 text-slate-400 hover:text-fly-600 hover:bg-fly-50 rounded-lg transition-all" title="تعديل">
                                        <Edit size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(company.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="حذف">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="space-y-2 text-sm text-slate-600">
                                <div className="flex items-center gap-2">
                                    <Hammer size={14} className="text-slate-400" />
                                    <span>{company.contactName || 'غير محدد'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone size={14} className="text-slate-400" />
                                    <span dir="ltr" className="text-right">{company.phone}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Mail size={14} className="text-slate-400" />
                                    <span>{company.email || '-'}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                    {filteredCompanies.length === 0 && (
                        <div className="col-span-full text-center py-10 text-slate-400">
                            لا توجد شركات صيانة مسجلة
                        </div>
                    )}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
                    <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 animate-scaleIn">
                        <div className="bg-white p-6 border-b border-slate-200 flex justify-between items-center shrink-0">
                            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                                <div className="bg-fly-50 p-2 rounded-lg">
                                    <Building2 className="w-5 h-5 text-fly-600" />
                                </div>
                                <span>{editingCompany ? 'تعديل بيانات الشركة' : 'إضافة شركة صيانة جديدة'}</span>
                            </h2>
                            <button type="button" onClick={handleCloseForm} className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100">✕</button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-slate-50/50">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">اسم الشركة</label>
                                <input required type="text" className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm" 
                                    value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">التخصص (سباكة، كهرباء...)</label>
                                <input required type="text" className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm" 
                                    value={formData.specialty} onChange={e => setFormData({...formData, specialty: e.target.value})} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">اسم المسؤول</label>
                                    <input type="text" className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm" 
                                        value={formData.contactName} onChange={e => setFormData({...formData, contactName: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1.5">رقم الهاتف</label>
                                    <input required type="text" className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm" 
                                        value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">البريد الإلكتروني</label>
                                <input type="email" className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm" 
                                    value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">الحالة</label>
                                <select className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm"
                                    value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as any})}>
                                    <option value="Active">نشط</option>
                                    <option value="Inactive">غير نشط</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-4 bg-white border-t border-slate-200 flex justify-end gap-3 shrink-0">
                            <button type="button" onClick={handleCloseForm} className="px-6 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-xs font-bold font-bold">إلغاء</button>
                            <button type="submit" className="px-8 py-2.5 bg-fly-600 text-white rounded-xl font-bold hover:bg-fly-700 shadow-lg shadow-fly-600/20 transition-all text-xs font-bold font-bold">
                                {editingCompany ? 'تحديث البيانات' : 'إضافة الشركة'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default MaintenanceCompanies;
